import React, { useState } from 'react';
import css from '../calculatorStyles.module.css';
import { calculateContainer } from '../engine/containerEngine';
import { MANUFACTURERS } from '../config/containerConfig';
import ResultDisplay from './ResultDisplay';
import { formatLabel } from '../calculatorUtils';

const ContainerCalculator = () => {
  const [customPrice, setCustomPrice] = useState(false);
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [domYear, setDomYear] = useState('');
  const [jumps, setJumps] = useState('');
  const [maxCanopySqft, setMaxCanopySqft] = useState('');
  const [minCanopySqft, setMinCanopySqft] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const mfrOptions = Object.keys(MANUFACTURERS).sort();
  const modelOptions = manufacturer
    ? Object.keys(MANUFACTURERS[manufacturer]?.models || {}).sort()
    : [];

  const handleManufacturerChange = e => {
    setManufacturer(e.target.value);
    setModel('');
    setResults(null);
  };

  const handleCalculate = () => {
    setError(null);
    try {
      if (!domYear) {
        throw new Error('Please enter a DOM year.');
      }
      const params = {
        domYear: parseInt(domYear, 10),
        jumps: jumps !== '' ? parseInt(jumps, 10) : undefined,
        maxCanopySqft: maxCanopySqft ? parseFloat(maxCanopySqft) : undefined,
        minCanopySqft: minCanopySqft ? parseFloat(minCanopySqft) : undefined,
        heightCm: heightCm ? parseFloat(heightCm) : undefined,
        weightKg: weightKg ? parseFloat(weightKg) : undefined,
      };
      if (customPrice) {
        if (!newPrice) {
          throw new Error('Please enter the new price.');
        }
        params.customNewPriceEur = parseFloat(newPrice);
      } else {
        if (!manufacturer) {
          throw new Error('Please select a manufacturer.');
        }
        if (!model) {
          throw new Error('Please select a model.');
        }
        params.manufacturer = manufacturer;
        params.model = model;
      }
      const result = calculateContainer(params);
      setResults(result);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReset = () => {
    setCustomPrice(false);
    setManufacturer('');
    setModel('');
    setNewPrice('');
    setDomYear('');
    setJumps('');
    setMaxCanopySqft('');
    setMinCanopySqft('');
    setHeightCm('');
    setWeightKg('');
    setResults(null);
    setError(null);
  };

  return (
    <div className={css.calculator}>
      <div className={css.card}>
        <label className={css.checkboxLabel}>
          <input
            type="checkbox"
            checked={customPrice}
            onChange={e => { setCustomPrice(e.target.checked); setResults(null); }}
          />
          My model is not listed
        </label>
        <div className={css.grid}>
          {customPrice ? (
            <>
              <div className={css.field}>
                <label className={css.label}>New Price (EUR)</label>
                <input
                  type="number"
                  className={css.input}
                  value={newPrice}
                  onChange={e => setNewPrice(e.target.value)}
                  placeholder="e.g. 3500"
                />
              </div>
            </>
          ) : (
            <>
              <div className={css.field}>
                <label className={css.label}>Manufacturer</label>
                <select
                  className={css.select}
                  value={manufacturer}
                  onChange={handleManufacturerChange}
                >
                  <option value="">Select...</option>
                  {mfrOptions.map(m => (
                    <option key={m} value={m}>{formatLabel(m)}</option>
                  ))}
                </select>
              </div>
              <div className={css.field}>
                <label className={css.label}>Model</label>
                <select
                  className={css.select}
                  value={model}
                  onChange={e => { setModel(e.target.value); setResults(null); }}
                  disabled={!manufacturer}
                >
                  <option value="">Select...</option>
                  {modelOptions.map(m => (
                    <option key={m} value={m}>{formatLabel(m)}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          <div className={css.field}>
            <label className={css.label}>DOM Year</label>
            <input
              type="number"
              className={css.input}
              value={domYear}
              onChange={e => setDomYear(e.target.value)}
              placeholder="e.g. 2020"
            />
          </div>
          <div className={css.field}>
            <label className={css.label}>Jumps</label>
            <input
              type="number"
              className={css.input}
              value={jumps}
              onChange={e => setJumps(e.target.value)}
              placeholder="e.g. 500"
              min="0"
            />
          </div>
          <div className={css.field}>
            <label className={css.label}>Holds Max Canopy Size (sqft)</label>
            <input
              type="number"
              className={css.input}
              value={maxCanopySqft}
              onChange={e => setMaxCanopySqft(e.target.value)}
              placeholder="e.g. 170"
            />
          </div>
          <div className={css.field}>
            <label className={css.label}>Holds Min Canopy Size (sqft)</label>
            <input
              type="number"
              className={css.input}
              value={minCanopySqft}
              onChange={e => setMinCanopySqft(e.target.value)}
              placeholder="e.g. 120"
            />
          </div>
          <div className={css.field}>
            <label className={css.label}>Height (cm)</label>
            <input
              type="number"
              className={css.input}
              value={heightCm}
              onChange={e => setHeightCm(e.target.value)}
              placeholder="e.g. 178"
            />
          </div>
          <div className={css.field}>
            <label className={css.label}>Weight (kg)</label>
            <input
              type="number"
              className={css.input}
              value={weightKg}
              onChange={e => setWeightKg(e.target.value)}
              placeholder="e.g. 80"
            />
          </div>
        </div>
        <div className={css.actions}>
          <button type="button" className={css.btnPrimary} onClick={handleCalculate}>
            Calculate
          </button>
          <button type="button" className={css.btnSecondary} onClick={handleReset}>
            Reset
          </button>
        </div>
      </div>
      {error && (
        <div className={`${css.error} ${css.errorVisible}`} role="alert">
          <strong>Error</strong>
          {error}
        </div>
      )}
      {results && <ResultDisplay {...results} />}
    </div>
  );
};

export default ContainerCalculator;
